import { Component, OnDestroy, OnInit } from '@angular/core';
import { App, AppCategoryGroup, AppCategoryOption, AppKey, AppProfile, AppStatus } from './app/app';
import { ActivatedRoute, Router } from '@angular/router';
import { AppsService } from './apps.service';
import { PageService } from '../core/page.service';
import { environment } from '../../environments/environment';
import { UserService } from '../core/user/user.service';
import { Breakpoint, fromMediaQuery } from '../core/helpers/utils';
import { SessionService } from '../core/session.service';
import { AnalyticsPage } from '../core/internal-analytics.service';
import { MessageService } from 'primeng/api';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface AppsFilterChip {
  id: string;
  label: string;
  translate: boolean;
}

const CONNECTED_CHIP_ID = 'status:connected';
const CATEGORY_CHIP_PREFIX = 'category:';

@Component({
  selector: 'rosie-apps',
  templateUrl: './apps.component.html',
  styleUrls: ['./apps.component.scss'],
  standalone: false
})
export class AppsComponent implements OnInit, OnDestroy {
  AppStatus = AppStatus;
  AppKey = AppKey;
  selectedApp: App;
  isMobileVersion: boolean = false;
  searchControl = new UntypedFormControl('');
  private searchSub: Subscription;
  private refreshStateSub: Subscription;

  constructor(private router: Router, private route: ActivatedRoute, private appsService: AppsService, private session: SessionService,
    private page: PageService, private user: UserService, private messageService: MessageService) {
    this.page.initializePage(`Rosie - Integrations`, AnalyticsPage.Integrations);
    this.refreshStateSub = this.session.refreshState.subscribe(() => this.reloadApps());
  }

  get apps(): App[] {
    return this.appsService.apps;
  }

  get appGroups(): AppCategoryGroup[] {
    return this.appsService.appGroups;
  }

  get categoryOptions(): AppCategoryOption[] {
    return this.appsService.availableCategories;
  }

  get hasResults(): boolean {
    return this.appGroups.length > 0;
  }

  get isProduction(): boolean {
    return environment.name === 'production' || environment.name === 'sandbox';
  }

  ngOnInit(): void {
    this.initSearch();
    this.route.paramMap
      .subscribe((paramMap) => {
        const selectedAppKey = paramMap.get('id') as AppKey;
        this.selectedApp = this.apps.find((app) => app.key === selectedAppKey);
        const subTitle = this.selectedApp?.name ? `- ${this.selectedApp.name}` : '';
        this.page.initializePage(`Rosie - Integrations ${subTitle}`);
        this.fetchAppRelatedData();

        const requestedCategory = paramMap.get('category');
        if (requestedCategory) {
          this.preselectCategory(requestedCategory);
        }
      });
    fromMediaQuery(Breakpoint.UpToMedium).subscribe(matches => this.isMobileVersion = matches);
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.refreshStateSub?.unsubscribe();
  }

  // An account switch while this page is open drops the previous business's
  // catalog and filters in AppsService before we get here, so the search box
  // follows the service and the selected app is re-read from the new catalog.
  private reloadApps(): void {
    this.searchControl.setValue(this.appsService.searchTerm, { emitEvent: false });
    this.appsService.getAvailableApps().subscribe(() => {
      if (this.selectedApp) {
        this.selectedApp = this.apps.find((app) => app.key === this.selectedApp.key);
      }
    });
  }

  // ---- Search ---------------------------------------------------------------

  get isSearchActive(): boolean {
    return this.appsService.hasSearchTerm;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  private initSearch(): void {
    this.searchControl.setValue(this.appsService.searchTerm, { emitEvent: false });
    this.searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((term: string) => this.appsService.setSearchTerm(term));
  }

  // ---- Filters (Type / Status) ---------------------------------------------

  get hasAdvancedFilters(): boolean {
    return this.appsService.hasAdvancedFilters;
  }

  get advancedFilterCount(): number {
    return this.appsService.advancedFilterCount;
  }

  get hasAnyActiveFilters(): boolean {
    return this.appsService.hasAnyActiveFilters;
  }

  get connectedOnly(): boolean {
    return this.appsService.connectedOnly;
  }

  isCategorySelected(key: string): boolean {
    return this.appsService.isCategorySelected(key);
  }

  toggleCategory(key: string): void {
    this.appsService.toggleCategory(key);
  }

  toggleConnectedOnly(): void {
    this.appsService.setConnectedOnly(!this.appsService.connectedOnly);
  }

  get filterChips(): AppsFilterChip[] {
    const chips: AppsFilterChip[] = this.appsService.selectedCategories.map((key) => ({
      id: `${CATEGORY_CHIP_PREFIX}${key}`,
      label: this.appsService.getCategoryLabel(key),
      translate: false,
    }));
    if (this.appsService.connectedOnly) {
      chips.push({ id: CONNECTED_CHIP_ID, label: 'APPS.STATUS_CONNECTED', translate: true });
    }
    return chips;
  }

  trackCategory(_index: number, category: AppCategoryOption): string {
    return category.key;
  }

  trackFilterChip(_index: number, chip: AppsFilterChip): string {
    return chip.id;
  }

  removeChip(id: string): void {
    if (id === CONNECTED_CHIP_ID) {
      this.appsService.setConnectedOnly(false);
      return;
    }
    if (id.startsWith(CATEGORY_CHIP_PREFIX)) {
      this.toggleCategory(id.slice(CATEGORY_CHIP_PREFIX.length));
    }
  }

  clearAdvancedFilters(): void {
    this.appsService.clearAdvancedFilters();
  }

  clearAllFilters(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.appsService.clearAllFilters();
  }

  private preselectCategory(key: string): void {
    const isKnownCategory = this.appsService.categories.some((category) => category.key === key);
    if (!isKnownCategory || this.appsService.isCategorySelected(key)) {
      return;
    }
    this.appsService.toggleCategory(key);
  }

  // ---- Cards ----------------------------------------------------------------

  trackGroup(_index: number, group: AppCategoryGroup): string {
    return group.key;
  }

  trackApp(_index: number, app: App): string {
    return app.id;
  }

  seeApp(appKey: AppKey) {
    if (appKey === AppKey.OtherSchedulingTools) {
      // Open contact form or external link
      window.open('mailto:support@heyrosie.com?subject=Integration Request - Other Scheduling Tools', '_blank');
      return;
    }
    this.router.navigate(['/integrations', { id: appKey }]);
  }

  getLogo(app: App): string {
    return `assets/images/${app.referenceId}.svg`;
  }

  private fetchAppRelatedData() {
    if (this.selectedApp && !this.selectedApp.isIntegrating) {
      this.selectedApp.inProgress = true;
      if (this.selectedApp.isInstalled) {
        this.appsService.getAppData(this.selectedApp).subscribe((appData: AppProfile) => {
          this.selectedApp.details.profile = appData;
          this.selectedApp.inProgress = false;
        }, () => this.selectedApp.inProgress = false);
      } else {
        this.selectedApp.inProgress = false;
      }
    }
  }

  getAppStatus(app: App): string {
    if (app.status === AppStatus.ComingSoon) {
      return 'coming-soon';
    }
    if (app.status === AppStatus.Installed) {
      return 'installed';
    }
    if (app.status === AppStatus.Pending) {
      return 'pending';
    }
    return '';
  }

  getAppStatusCopy(app: App): string {
    if (app.status === AppStatus.ComingSoon) {
      return 'COMING SOON';
    }
    if (app.status === AppStatus.Installed) {
      return 'CONNECTED';
    }
    if (app.status === AppStatus.Pending) {
      return 'PENDING';
    }
    return '';
  }

  requestNewIntegration(event: Event) {
    event.stopPropagation();
    const el: any = document.querySelector('.request-integration-intercom-trigger');
    if (el) {
      el.click();
    }
  }
}
